/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { Colors, Shadows } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { ServiceIconSize, ServiceIcon } from '../ServiceIcon';
import { ServiceIconShell } from '../ServiceIconShell';
import { AppStatus } from '../../types';
import { AppStatusTag } from '../AppStatusTag';

export type Size = 'small' | 'medium' | 'large' | 'extra-large';

type Props = {
  /** The accent color which fills the frame around the app icon. */
  appColor: string;

  /** The name of the app being displayed. */
  appName: string;

  /** The src of the app icon to render. */
  appIconSrc: string;

  /** The size of the component to render. */
  size: Size;

  /** Whether or not to render in loading mode. */
  isLoading: boolean;

  /** Optionally include an app status tag to be displayed over the icon
   * (beta, upcoming, premium, etc)
   */
  statusTag?: AppStatus;
};

const Styles = labelStyles('AppIconFramed', {
  root: (props: Props) => [
    {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      justifySelf: 'center',
      height: 90,
      width: 90,
      borderRadius: 10,
      backgroundColor: props.appColor,
      transition: 'background-color 300ms',
    },
    withModifier(
      props.size,
      {
        'extra-large': {
          height: 265,
          width: 265,
        },
        large: {
          height: 230,
          width: 230,
        },
        medium: {
          height: 190,
          width: 190,
        },
        small: {},
      }[props.size]
    ),
  ],
  serviceIconWrap: css({
    position: 'relative',
  }),
  appStatusTag: (props: Props) => [
    css({
      position: 'absolute',
      top: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      lineHeight: '15px',
      display: 'block',
      boxShadow: Shadows.elevation5,
      borderRadius: '5px',
    }),
    props.size === 'extra-large' &&
      withModifier(props.size, {
        lineHeight: '20px',
      }),
  ],
});

const appIconSizes: { [size in Size]: ServiceIconSize } = {
  small: 60,
  medium: 120,
  large: 120,
  'extra-large': 150,
};

/**
 * Renders an App Icon, framed by a colored border in the accent color of the given app.
 */
export const AppIconFramed = (props: Props) => {
  const { appName, appIconSrc, size, statusTag, isLoading } = props;

  const appStatusSize = size === 'extra-large' ? 'medium' : 'small';

  return (
    <span css={Styles.root(props)}>
      <span css={Styles.serviceIconWrap}>
        {!!statusTag && (
          <span css={Styles.appStatusTag(props)}>
            <AppStatusTag size={appStatusSize} status={statusTag} />
          </span>
        )}
        {isLoading ? (
          <ServiceIconShell
            height={appIconSizes[size]}
            width={appIconSizes[size]}
          />
        ) : (
          <ServiceIcon
            serviceName={appName}
            shadow={Shadows.elevation6}
            size={appIconSizes[size]}
            src={appIconSrc}
          />
        )}
      </span>
    </span>
  );
};

AppIconFramed.defaultProps = {
  appColor: Colors.grey100 as string,
  appIconSrc: '',
  appName: '',
  isLoading: false,
  size: 'small',
};
